/**
 * RemittanceHistoryDetailedPage.js v2 — Inspired by sendmoney.cubasolution.com
 *
 * Fase A: pagination + sort + state filter + auto-computed USDT.
 * Fase B: tab Gráficos funcional (CUP/MLC/USD/Profit) + Export PDF + Column picker.
 * Fase C: tooltips, sticky header, empty state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, RefreshCw, FileDown, BarChart2, Table as TableIcon, Filter,
  Edit2, Calendar, Activity, ChevronsUpDown, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, LabelList,
} from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_BADGE = {
  DELIVERED:            { label: 'Entregada',         klass: 'bg-emerald-500 text-white' },
  PENDING_DELIVERY:     { label: 'Pendiente Entrega', klass: 'bg-teal-700 text-white' },
  ASSIGNED_TO_PROVIDER: { label: 'Pendiente Entrega', klass: 'bg-teal-700 text-white' },
  CREATED:              { label: 'Creada',            klass: 'bg-slate-400 text-white' },
  REJECTED:             { label: 'Rechazada',         klass: 'bg-rose-500 text-white' },
  CANCELLED:            { label: 'Cancelada',         klass: 'bg-slate-500 text-white' },
};

const STATE_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'PENDING_DELIVERY', label: 'Pendiente Entrega' },
  { value: 'DELIVERED', label: 'Entregada' },
  { value: 'REJECTED', label: 'Rechazada' },
  { value: 'CANCELLED', label: 'Cancelada' },
];

const PAGE_SIZES = [10, 25, 50, 100];

const ALL_COLUMNS = [
  { key: 'fecha', label: 'Fecha', sort: 'created_at' },
  { key: 'tipo', label: 'Tipo', sort: null },
  { key: 'amount', label: 'Cantidad', sort: 'amount' },
  { key: 'rate', label: 'Tasa', sort: 'rate' },
  { key: 'received', label: 'Monto Recibido', sort: 'amount_received' },
  { key: 'usuario', label: 'Enviada por', sort: null },
  { key: 'beneficiario', label: 'Beneficiario', sort: null },
  { key: 'delivered_by', label: 'Entregada Por', sort: null },
  { key: 'asset_bought', label: '{a} Comprado', sort: 'asset_bought' },
  { key: 'asset_buy_price', label: 'Precio {a} Compra', sort: null },
  { key: 'asset_sold', label: '{a} Vendido', sort: 'asset_sold' },
  { key: 'asset_sell_price', label: 'Precio {a} Venta', sort: null },
  { key: 'debt', label: 'Deuda', sort: null },
  { key: 'profit', label: 'Ganancia', sort: 'profit' },
  { key: 'actions', label: ' ', sort: null },
];

const fmtMoney = (n, decimals = 2) =>
  n == null || Number.isNaN(Number(n))
    ? '—'
    : `$${Number(n).toLocaleString('es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : '—';

const monthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

const dayLabel = (d) =>
  d ? `${String(parseInt(d.slice(8, 10)))} ${['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'][parseInt(d.slice(5, 7)) - 1]}` : '';

// Standalone chart card (defined outside the page so React doesn't recreate it
// on every render — required by react/no-unstable-nested-components).
const ChartCard = ({ title, dataKey, color, data, period, isLine = false }) => (
  <Card className="border-0 shadow-sm p-4 bg-slate-50/40" data-testid={`chart-${title.toLowerCase().replace(/\s+/g,'-')}`}>
    <h3 className="text-lg font-bold text-teal-700">{title}</h3>
    <p className="text-[11px] text-slate-500 mb-3">{period} · Últimos 7 días</p>
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        {isLine ? (
          <LineChart data={data} margin={{ top: 20, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="day" tickFormatter={dayLabel} fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip formatter={(v) => Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2 })} labelFormatter={dayLabel} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={{ r: 4 }}>
              <LabelList dataKey={dataKey} position="top" formatter={(v) => v ? Number(v).toLocaleString('es-ES', { maximumFractionDigits: 0 }) : ''} fontSize={11} fill={color} />
            </Line>
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ top: 24, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="day" tickFormatter={dayLabel} fontSize={11} />
            <YAxis fontSize={11} />
            <Tooltip
              formatter={(v) => Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
              labelFormatter={(d) => `Fecha: ${dayLabel(d)}`}
            />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]}>
              <LabelList dataKey={dataKey} position="top" formatter={(v) => v ? Number(v).toLocaleString('es-ES', { maximumFractionDigits: 0 }) : ''} fontSize={11} fill={color} />
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  </Card>
);

// Inline-editable cell (defined outside the page to avoid react/no-unstable-nested-components).
const InlineCell = ({
  row, field, value, format, align = 'right', decimals = 2,
  inlineEdit, setInlineEdit, startInline, commitInline, cancelInline,
}) => {
  const isEditing = inlineEdit?.id === row.id && inlineEdit?.field === field;
  const editable = row.is_editable_financials;
  if (isEditing) {
    return (
      <input
        type="number"
        step="any"
        autoFocus
        value={inlineEdit.value}
        onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}
        onBlur={commitInline}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commitInline(); }
          else if (e.key === 'Escape') cancelInline();
        }}
        className={`w-24 px-2 py-1 border border-teal-400 rounded text-xs font-mono ${align === 'right' ? 'text-right' : ''}`}
        data-testid={`inline-${field}-${row.id.slice(0, 8)}`}
      />
    );
  }
  const formatted = format ? format(value, decimals) : (value != null ? Number(value).toFixed(decimals) : '—');
  return (
    <button
      type="button"
      onClick={() => editable && startInline(row.id, field, value)}
      disabled={!editable}
      className={`font-mono ${align === 'right' ? 'text-right w-full' : ''} ${editable ? 'hover:bg-teal-50 hover:border-teal-300 border border-dashed border-transparent rounded px-1 cursor-text' : 'cursor-default'}`}
      title={editable ? 'Click para editar' : ''}
      data-testid={`cell-${field}-${row.id.slice(0, 8)}`}
    >
      {formatted}
    </button>
  );
};

const RemittanceHistoryDetailedPage = () => {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState('detail');
  const [items, setItems] = useState([]);
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [assetLabel, setAssetLabel] = useState('USDT');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [range, setRange] = useState(monthRange());
  const [state, setState] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('rhd_visible_cols') || 'null');
      return stored && Array.isArray(stored) ? stored : ALL_COLUMNS.map((c) => c.key);
    } catch { return ALL_COLUMNS.map((c) => c.key); }
  });
  const [colsDialogOpen, setColsDialogOpen] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
      if (range.start) params.start_date = `${range.start}T00:00:00Z`;
      if (range.end)   params.end_date   = `${range.end}T23:59:59Z`;
      if (state !== 'all') params.state = state;
      const r = await axios.get(`${API}/remittances/history-detailed`, { headers: getAuthHeaders(), params });
      setItems(r.data.items || []);
      setTotalReceived(r.data.total_amount_uyu || 0);
      setTotalCount(r.data.total_count || 0);
      setTotalPages(r.data.total_pages || 1);
      setAssetLabel(r.data.asset_label || 'USDT');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo cargar el historial'));
    } finally {
      setLoading(false);
    }
  };

  const loadCharts = async () => {
    try {
      const params = {};
      if (range.start) params.start_date = `${range.start}T00:00:00Z`;
      if (range.end)   params.end_date   = `${range.end}T23:59:59Z`;
      const r = await axios.get(`${API}/remittances/charts/daily`, { headers: getAuthHeaders(), params });
      setChartData(r.data);
    } catch {
      setChartData(null);
    }
  };

  useEffect(() => { load(); }, [range.start, range.end, state, sortBy, sortDir, page, pageSize]);
  useEffect(() => { if (tab === 'charts') loadCharts(); }, [tab, range.start, range.end]);
  // Wrapper handlers reset pagination to page 1 whenever the user changes
  // a filter (range / state / page size). Two setState in a row is fine in
  // React 18 — it batches.
  const onRangeStart = useCallback((v) => {
    setPage(1);
    setRange((p) => ({ ...p, start: v }));
  }, []);
  const onRangeEnd = useCallback((v) => {
    setPage(1);
    setRange((p) => ({ ...p, end: v }));
  }, []);
  const onStateChg = useCallback((v) => {
    setPage(1);
    setState(v);
  }, []);
  const onPageSizeChg = useCallback((v) => {
    setPage(1);
    setPageSize(Number(v));
  }, []);

  const filtered = useMemo(() => {
    if (!query) return items;
    const t = query.toLowerCase();
    return items.filter((it) =>
      [it.user_email, it.delivered_by_email, it.beneficiary_card, it.created_at]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(t))
    );
  }, [items, query]);

  const toggleSort = (field) => {
    if (!field) return;
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const sortIcon = (field) => {
    if (!field) return null;
    if (sortBy !== field) return <ChevronsUpDown className="w-3 h-3 inline-block ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 inline-block ml-1 text-teal-600" />
      : <ChevronDown className="w-3 h-3 inline-block ml-1 text-teal-600" />;
  };

  const openEdit = (it) => {
    setEditTarget(it);
    setEditForm({
      usdt_cost: it.asset_bought ?? '', usdt_buy_price: it.asset_buy_price ?? '',
      usdt_sale_uyu: it.asset_sold ?? '', usdt_sell_price: it.asset_sell_price ?? '',
      bridge_leg2_rate: it.rate ?? '', amount_received: it.amount_received ?? '',
      gross_profit_uyu: it.profit ?? '', debt: it.debt ?? '',
    });
  };

  // Inline cell editing: keeps track of which (id, field) is being edited
  // and saves on blur or Enter. Esc cancels. The backend auto-recalculates
  // dependent fields (profit, debt) after the PATCH so we just reload.
  const [inlineEdit, setInlineEdit] = useState(null); // { id, field, value }

  const startInline = (id, field, value) => {
    setInlineEdit({ id, field, value: value == null ? '' : String(value) });
  };
  const cancelInline = () => setInlineEdit(null);
  const commitInline = async () => {
    if (!inlineEdit) return;
    const { id, field, value } = inlineEdit;
    const num = parseFloat(value);
    setInlineEdit(null);
    if (Number.isNaN(num)) return;
    // Find the original value — if unchanged, skip the request.
    const it = items.find((x) => x.id === id);
    const fieldMap = {
      asset_bought: 'usdt_cost',
      asset_sold: 'usdt_sale_uyu',
      rate: 'bridge_leg2_rate',
      amount_received: 'amount_received',
      asset_buy_price: 'usdt_buy_price',
      asset_sell_price: 'usdt_sell_price',
    };
    const apiField = fieldMap[field];
    if (!apiField) return;
    const originalVal = it ? it[field] : null;
    if (originalVal != null && Number(originalVal) === num) return;
    try {
      await axios.patch(`${API}/operations/${id}/financials`, { [apiField]: num }, { headers: getAuthHeaders() });
      toast.success('Actualizado · recalculando…');
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo actualizar'));
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        const num = parseFloat(v);
        if (!Number.isNaN(num)) payload[k] = num;
      });
      await axios.patch(`${API}/operations/${editTarget.id}/financials`, payload, { headers: getAuthHeaders() });
      toast.success('Datos financieros actualizados');
      setEditTarget(null);
      await load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo actualizar'));
    } finally {
      setSavingEdit(false);
    }
  };

  const period = `${range.start.split('-').reverse().join('/')} - ${range.end.split('-').reverse().join('/')}`;
  const visible = (k) => visibleCols.includes(k);
  const colLabel = (c) => (c.label || '').replace('{a}', assetLabel);

  const toggleColumn = (k) => {
    const next = visibleCols.includes(k) ? visibleCols.filter((x) => x !== k) : [...visibleCols, k];
    setVisibleCols(next);
    localStorage.setItem('rhd_visible_cols', JSON.stringify(next));
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      // Pull ALL rows in range (max 1000) to PDF, not just the page.
      const params = { page: 1, page_size: 1000, sort_by: sortBy, sort_dir: sortDir };
      if (range.start) params.start_date = `${range.start}T00:00:00Z`;
      if (range.end) params.end_date = `${range.end}T23:59:59Z`;
      if (state !== 'all') params.state = state;
      const r = await axios.get(`${API}/remittances/history-detailed`, { headers: getAuthHeaders(), params });
      const rows = (r.data.items || []).map((it) => ([
        fmtDateTime(it.created_at), it.currency_to || '—',
        fmtMoney(it.amount), it.rate != null ? Number(it.rate).toFixed(4) : '—',
        fmtMoney(it.amount_received), it.user_email || '—',
        it.beneficiary_card || '—', it.delivered_by_email || '—',
        fmtMoney(it.asset_bought), it.asset_buy_price != null ? Number(it.asset_buy_price).toFixed(6) : '—',
        fmtMoney(it.asset_sold), fmtMoney(it.asset_sell_price),
        fmtMoney(it.debt, 3), fmtMoney(it.profit),
      ]));
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      doc.setFontSize(14);
      doc.setTextColor(15, 118, 110);
      doc.text(`Historial de Remesas — ${period}`, 40, 36);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Total UYU recibido: $${r.data.total_amount_uyu.toLocaleString('es-ES', { minimumFractionDigits: 2 })}  ·  ${r.data.total_count} remesas`, 40, 52);
      autoTable(doc, {
        startY: 70,
        head: [[
          'Fecha', 'Tipo', 'Cantidad', 'Tasa', 'Recibido', 'Usuario',
          'Beneficiario', 'Entregada por', `${assetLabel} Compr.`, `Precio ${assetLabel} C.`,
          `${assetLabel} Vend.`, `Precio ${assetLabel} V.`, 'Deuda', 'Ganancia',
        ]],
        body: rows,
        styles: { fontSize: 7, cellPadding: 3 },
        headStyles: { fillColor: [15, 118, 110], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
      });
      const fileName = `historial-remesas-${range.start}-${range.end}.pdf`;
      doc.save(fileName);
      toast.success(`PDF exportado: ${fileName}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo exportar el PDF'));
    } finally {
      setExporting(false);
    }
  };

  // ── Chart helpers (ChartCard defined at file top) ────────────────────────

  return (
    <div className="space-y-4" data-testid="remittance-history-detailed">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-teal-700">Historial de Remesas</h1>
          <p className="text-xs text-slate-500 mt-1 tracking-wide">{period}</p>
        </div>
        <Card className="px-5 py-3 border-0 bg-emerald-50/70 shadow-sm" data-testid="kpi-total-received">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">$</div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Total UYU Recibido</p>
              <p className="text-xl font-bold text-emerald-700 font-mono">{fmtMoney(totalReceived)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-1.5">
          <Calendar className="w-4 h-4 text-teal-600" />
          <Input type="date" value={range.start} onChange={(e) => onRangeStart(e.target.value)}
                 className="border-0 h-7 w-32 text-xs px-0" data-testid="date-start" />
          <span className="text-xs text-slate-400">→</span>
          <Input type="date" value={range.end} onChange={(e) => onRangeEnd(e.target.value)}
                 className="border-0 h-7 w-32 text-xs px-0" data-testid="date-end" />
        </div>
        <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-pdf-btn">
          <FileDown className="w-4 h-4 mr-1" /> {exporting ? 'Generando…' : 'Exportar PDF'}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { load(); if (tab === 'charts') loadCharts(); }} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
        </Button>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-2 bg-white rounded-lg border overflow-hidden">
        <button onClick={() => setTab('charts')}
          className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition ${tab === 'charts' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:bg-slate-50'}`}
          data-testid="tab-charts">
          <BarChart2 className="w-4 h-4" /> Gráficos
        </button>
        <button onClick={() => setTab('detail')}
          className={`flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition ${tab === 'detail' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:bg-slate-50'}`}
          data-testid="tab-detail">
          <TableIcon className="w-4 h-4" /> Historial Detallado
        </button>
      </div>

      {tab === 'charts' ? (
        <div className="space-y-4">
          {!chartData ? (
            <Card className="p-12 text-center"><p className="text-slate-400">Cargando gráficos…</p></Card>
          ) : (
            <>
              <ChartCard title="Remesas CUP" dataKey="amount" color="#2563eb" data={chartData.cup} period={period} />
              <ChartCard title="Remesas MLC" dataKey="amount" color="#fb7185" data={chartData.mlc} period={period} />
              <ChartCard title="Remesas USD" dataKey="amount" color="#ef4444" data={chartData.usd} period={period} />
              <Card className="border-0 shadow-sm p-4 bg-slate-50/40" data-testid="chart-ganancias-usdt">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-bold text-teal-700">Ganancias {chartData.asset_label}</h3>
                    <p className="text-[11px] text-slate-500">{period} · Últimos 7 días</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Ganancias Totales</p>
                    <p className="text-xl font-bold text-emerald-700 font-mono">${chartData.total_profit?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.profit} margin={{ top: 24, right: 16, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tickFormatter={dayLabel} fontSize={11} />
                      <YAxis fontSize={11} />
                      <Tooltip formatter={(v) => Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2 })} labelFormatter={(d) => `Fecha: ${dayLabel(d)}`} />
                      <Bar dataKey="profit_usdt" fill="#10b981" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="profit_usdt" position="top" formatter={(v) => v ? Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 }) : ''} fontSize={11} fill="#10b981" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </>
          )}
        </div>
      ) : (
        <Card className="border-0 shadow-sm" data-testid="history-detail-card">
          <div className="p-4 border-b">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-bold text-slate-800">Historial Detallado de Ganancias</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)}
                       placeholder="Filtrar por usuario, beneficiario o fecha…"
                       className="pl-9 h-9 bg-slate-50 border-slate-200" data-testid="search-input" />
              </div>
              <Select value={state} onValueChange={onStateChg}>
                <SelectTrigger className="h-9 w-44 bg-white" data-testid="state-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATE_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => setColsDialogOpen(true)} data-testid="show-columns-btn">
                <Filter className="w-3.5 h-3.5 mr-1" /> Mostrar
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                <tr>
                  {ALL_COLUMNS.filter((c) => visible(c.key)).map((c) => (
                    <th key={c.key} className={`p-3 ${['amount','rate','received','asset_bought','asset_buy_price','asset_sold','asset_sell_price','debt','profit'].includes(c.key) ? 'text-right' : 'text-left'} ${c.sort ? 'cursor-pointer hover:text-teal-700 select-none' : ''}`}
                        onClick={() => toggleSort(c.sort)} data-testid={`th-${c.key}`}>
                      {colLabel(c)}{sortIcon(c.sort)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={visibleCols.length} className="p-8 text-center text-slate-400">Cargando…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={visibleCols.length} className="p-12 text-center">
                    <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-400">Sin movimientos en este rango.</p>
                    <p className="text-xs text-slate-400 mt-1">Probá cambiar las fechas o el filtro de estado.</p>
                  </td></tr>
                ) : filtered.map((it) => {
                  const sb = STATE_BADGE[it.state] || { label: it.state, klass: 'bg-slate-300 text-slate-700' };
                  return (
                    <tr key={it.id} className="border-t hover:bg-emerald-50/30 even:bg-slate-50/30" data-testid={`row-${it.id}`}>
                      {visible('fecha') && (
                        <td className="p-3 align-top">
                          <Badge className={`${sb.klass} font-medium rounded-full`}>● {sb.label}</Badge>
                          <div className="text-[11px] text-slate-500 mt-1">{fmtDateTime(it.created_at)}</div>
                        </td>
                      )}
                      {visible('tipo') && <td className="p-3 align-top"><Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{it.currency_to || 'CUP'}</Badge></td>}
                      {visible('amount') && <td className="p-3 align-top text-right font-mono">{fmtMoney(it.amount)}</td>}
                      {visible('rate') && <td className="p-3 align-top text-right font-mono"><InlineCell row={it} field="rate" value={it.rate} format={(v) => v != null ? Number(v).toFixed(4) : '—'} decimals={4} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} startInline={startInline} commitInline={commitInline} cancelInline={cancelInline} /></td>}
                      {visible('received') && (
                        <td className="p-3 align-top text-right">
                          <div className="font-mono font-semibold"><InlineCell row={it} field="amount_received" value={it.amount_received} format={fmtMoney} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} startInline={startInline} commitInline={commitInline} cancelInline={cancelInline} /></div>
                          <div className="text-[10px] mt-1 inline-block bg-blue-50 text-blue-700 rounded-full px-2 py-0.5 font-medium">{it.payment_method}</div>
                        </td>
                      )}
                      {visible('usuario') && (
                        <td className="p-3 align-top text-xs text-slate-600" data-testid={`sender-${it.id.slice(0,8)}`}>
                          {it.sender_label ? (
                            <>
                              <span className="font-semibold text-slate-900">{it.sender_label}</span>
                              {it.sender_role && (
                                <span className="ml-1 text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
                                  {it.sender_role === 'admin' ? 'CEO' : it.sender_role}
                                </span>
                              )}
                              {(it.gestor_info?.email || it.created_by_info?.email || it.user_email) && (
                                <div className="text-[10px] text-slate-400">
                                  {it.gestor_info?.email || it.created_by_info?.email || it.user_email}
                                </div>
                              )}
                            </>
                          ) : (it.user_email || '—')}
                        </td>
                      )}
                      {visible('beneficiario') && <td className="p-3 align-top text-xs font-mono text-slate-600">{it.beneficiary_card || '—'}</td>}
                      {visible('delivered_by') && <td className="p-3 align-top text-xs text-slate-600">{it.delivered_by_email || '—'}</td>}
                      {visible('asset_bought') && <td className="p-3 align-top text-right font-mono" title={`Costo: ${it.asset_buy_price ? (it.asset_buy_price * (it.asset_bought||0)).toFixed(2) : '—'} UYU`}><InlineCell row={it} field="asset_bought" value={it.asset_bought} format={fmtMoney} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} startInline={startInline} commitInline={commitInline} cancelInline={cancelInline} /></td>}
                      {visible('asset_buy_price') && <td className="p-3 align-top text-right font-mono"><InlineCell row={it} field="asset_buy_price" value={it.asset_buy_price} format={(v) => v != null ? Number(v).toFixed(6) : '—'} decimals={6} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} startInline={startInline} commitInline={commitInline} cancelInline={cancelInline} /></td>}
                      {visible('asset_sold') && <td className="p-3 align-top text-right font-mono"><InlineCell row={it} field="asset_sold" value={it.asset_sold} format={fmtMoney} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} startInline={startInline} commitInline={commitInline} cancelInline={cancelInline} /></td>}
                      {visible('asset_sell_price') && <td className="p-3 align-top text-right font-mono"><InlineCell row={it} field="asset_sell_price" value={it.asset_sell_price} format={fmtMoney} inlineEdit={inlineEdit} setInlineEdit={setInlineEdit} startInline={startInline} commitInline={commitInline} cancelInline={cancelInline} /></td>}
                      {visible('debt') && <td className="p-3 align-top text-right font-mono text-rose-600 font-semibold">{fmtMoney(it.debt, 3)}</td>}
                      {visible('profit') && <td className="p-3 align-top text-right font-mono text-emerald-600 font-semibold">{fmtMoney(it.profit)}</td>}
                      {visible('actions') && (
                        <td className="p-3 align-top text-right">
                          {it.is_editable_financials && (
                            <Button size="sm" variant="ghost" onClick={() => openEdit(it)} title="Editar financieros" data-testid={`edit-fin-${it.id.slice(0,8)}`}>
                              <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-wrap items-center justify-between p-3 border-t bg-slate-50/40 text-xs text-slate-500 gap-2">
            <div className="flex items-center gap-2">
              <span>Filas por página</span>
              <Select value={String(pageSize)} onValueChange={onPageSizeChg}>
                <SelectTrigger className="h-8 w-20 bg-white" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex-1 text-center font-medium" data-testid="page-info">
              Mostrando {(items.length ? (page - 1) * pageSize + 1 : 0)}-{(page - 1) * pageSize + items.length} de {totalCount} resultados
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(1)} data-testid="page-first"><ChevronsLeft className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="page-prev"><ChevronLeft className="w-3.5 h-3.5" /></Button>
              <span className="px-2 font-mono">{page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"><ChevronRight className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="page-last"><ChevronsRight className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        </Card>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar campos financieros</DialogTitle>
            <p className="text-xs text-slate-500">Remesa {editTarget?.id?.slice(0,8)} · cualquier campo en blanco no se modifica.</p>
          </DialogHeader>
          <form onSubmit={submitEdit} className="grid grid-cols-2 gap-3">
            {[
              ['usdt_cost', `${assetLabel} Comprado`], ['usdt_buy_price', `Precio ${assetLabel} Compra`],
              ['usdt_sale_uyu', `${assetLabel} Vendido`], ['usdt_sell_price', `Precio ${assetLabel} Venta`],
              ['bridge_leg2_rate', 'Tasa'], ['amount_received', 'Monto Recibido'],
              ['debt', 'Deuda'], ['gross_profit_uyu', 'Ganancia'],
            ].map(([k, lbl]) => (
              <div className="space-y-1" key={k}>
                <Label className="text-[11px]">{lbl}</Label>
                <Input type="number" step="any" value={editForm[k] ?? ''}
                       onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })}
                       data-testid={`edit-${k}`} />
              </div>
            ))}
            <DialogFooter className="col-span-2">
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>Cancelar</Button>
              <Button type="submit" disabled={savingEdit} className="bg-teal-600 hover:bg-teal-700" data-testid="save-financials-btn">
                {savingEdit ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Column picker dialog */}
      <Dialog open={colsDialogOpen} onOpenChange={setColsDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Columnas visibles</DialogTitle>
            <p className="text-xs text-slate-500">Tildá las columnas que quieras ver. Se guarda en este navegador.</p>
          </DialogHeader>
          <div className="space-y-1">
            {ALL_COLUMNS.filter((c) => c.label.trim()).map((c) => (
              <label key={c.key} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={visible(c.key)} onChange={() => toggleColumn(c.key)} data-testid={`col-toggle-${c.key}`} />
                <span className="text-sm">{colLabel(c)}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setColsDialogOpen(false)} className="bg-teal-600 hover:bg-teal-700">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemittanceHistoryDetailedPage;
